/**
 * SPEC §9.4 — colour is a map colouring of the provider's locations, so
 * neighbouring catchments contrast; alpha encodes support. Seams are drawn
 * explicitly between adjacent cells with different winners, because they carry
 * the map's message in a channel that survives colour blindness. Boundaries
 * mode drops the colour and keeps the seams.
 */
import { GeohashLayer } from '@deck.gl/geo-layers';
import { PathLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import { assignColours, bounds, neighbour, oklchToRgb, supportAlpha, type LocationEntry, type RGBA, type Theme } from '@latency-globe/shared';
import type { CellInfo, RenderMode } from './store.ts';

const INK: Record<Theme, [number, number, number]> = { dark: [236, 233, 226], light: [28, 28, 30] };
const NEUTRAL: Record<Theme, [number, number, number]> = { dark: [150, 150, 158], light: [120, 120, 128] };
const UNKNOWN: Record<Theme, [number, number, number]> = { dark: [90, 90, 96], light: [180, 180, 186] };

export interface Seam { from: [number, number]; to: [number, number] }

export function seams(cells: CellInfo[]): Seam[] {
  const winner = new Map(cells.map((c) => [c.cell, c.code]));
  const out: Seam[] = [];
  for (const c of cells) {
    const b = bounds(c.cell);
    const e = neighbour(c.cell, 'e'), n = neighbour(c.cell, 'n');
    if (e && winner.has(e) && winner.get(e) !== c.code) out.push({ from: [b.maxLon, b.minLat], to: [b.maxLon, b.maxLat] });
    if (n && winner.has(n) && winner.get(n) !== c.code) out.push({ from: [b.minLon, b.maxLat], to: [b.maxLon, b.maxLat] });
  }
  return out;
}

export class Colourist {
  private readonly rgbs: Map<string, [number, number, number]>;
  constructor(locations: Map<string, LocationEntry>, private theme: Theme) {
    const assigned = assignColours([...locations.values()], theme);
    this.rgbs = new Map([...assigned].map(([code, c]) => [code, oklchToRgb(c)]));
  }
  rgb(code: string): [number, number, number] | null {
    return this.rgbs.get(code) ?? null;
  }
  fill(c: CellInfo, mode: RenderMode): RGBA {
    const a = Math.round(255 * supportAlpha(c.total));
    if (mode === 'boundaries') return [...NEUTRAL[this.theme], Math.round(a * 0.55)] as RGBA;
    const rgb = this.rgb(c.code);
    return rgb ? [rgb[0], rgb[1], rgb[2], a] : [...UNKNOWN[this.theme], Math.round(a * 0.6)] as RGBA;
  }
  /** CSS colour for legends and panels. */
  css(code: string): string | null {
    const rgb = this.rgb(code);
    return rgb ? `rgb(${rgb[0]} ${rgb[1]} ${rgb[2]})` : null;
  }
}

export interface LayerInputs {
  cells: CellInfo[];
  colourist: Colourist;
  mode: RenderMode;
  theme: Theme;
  surface: [number, number, number];
  beforeId: string | undefined;
  version: string;
  selected: string | null;
  onHover: (info: CellInfo | null, x: number, y: number) => void;
  onClick: (info: CellInfo | null) => void;
}

export function buildLayers(i: LayerInputs): Layer[] {
  const s = seams(i.cells);
  const common = i.beforeId ? { beforeId: i.beforeId } : {};
  return [
    new GeohashLayer<CellInfo>({
      id: 'cells',
      data: i.cells,
      getGeohash: (d) => d.cell,
      getFillColor: (d) => i.colourist.fill(d, i.mode),
      getLineColor: [255, 255, 255, 0],
      filled: true,
      stroked: false,
      extruded: false,
      pickable: true,
      autoHighlight: true,
      highlightColor: [...INK[i.theme], 70],
      onHover: (p) => i.onHover((p.object as CellInfo | undefined) ?? null, p.x, p.y),
      onClick: (p) => { i.onClick((p.object as CellInfo | undefined) ?? null); return true; },
      updateTriggers: { getFillColor: [i.version, i.mode, i.theme] },
      parameters: { depthCompare: 'always' },
      ...common,
    }),
    // PathLayer, not LineLayer: LineLayer draws nothing under the interleaved globe (deck 9.4 + MapLibre 5).
    new PathLayer<Seam>({
      id: 'seam-halo',
      data: s,
      getPath: (d) => [d.from, d.to],
      getColor: [...i.surface, 210],
      getWidth: 2.5,
      widthUnits: 'pixels',
      capRounded: true,
      parameters: { depthCompare: 'always' },
      ...common,
    }),
    new PathLayer<Seam>({
      id: 'seams',
      data: s,
      getPath: (d) => [d.from, d.to],
      getColor: [...INK[i.theme], 235],
      getWidth: 1.5,
      widthUnits: 'pixels',
      capRounded: true,
      parameters: { depthCompare: 'always' },
      ...common,
    }),
    ...(i.selected ? [new GeohashLayer<{ cell: string }>({
      id: 'selected',
      data: [{ cell: i.selected }],
      getGeohash: (d) => d.cell,
      filled: false,
      stroked: true,
      getLineColor: [...INK[i.theme], 255],
      getLineWidth: 2,
      lineWidthUnits: 'pixels',
      parameters: { depthCompare: 'always' },
      ...common,
    })] : []),
  ];
}

export function hexToRgb(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i.exec(hex);
  return m ? [parseInt(m[1]!, 16), parseInt(m[2]!, 16), parseInt(m[3]!, 16)] : [20, 20, 24];
}
